import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api, onUnauthorized, resetUnauthorizedLatch } from './axiosConfig';
import { AuthMe } from '../types';

interface AuthContextType {
  me: AuthMe;
  loading: boolean;
  login: (returnTo?: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const EMPTY_ME: AuthMe = {
  authenticated: false,
  character: null,
  isAdmin: false,
  isDirector: false,
  isMember: false,
  canEdit: false,
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [me, setMe] = useState<AuthMe>(EMPTY_ME);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const response = await api.get<AuthMe>('/api/auth/me');
      setMe(response.data);
      // A good /me means the session is alive again, so a later 401 is news.
      if (response.data.authenticated) resetUnauthorizedLatch();
    } catch {
      setMe(EMPTY_ME);
    }
  };

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, []);

  // The session can die long after mount — /api/auth/me is only read once.
  // Without this the whole console stayed rendered as "logged in" while every
  // query underneath it 401'd, which reads as a broken app rather than an
  // expired login. Dropping to EMPTY_ME routes back to the gate, which is
  // exactly what App.tsx already does for a logged-out visitor.
  useEffect(() => onUnauthorized(() => setMe(EMPTY_ME)), []);

  const login = async (returnTo?: string) => {
    const response = await api.post<{ auth_url: string }>('/api/auth/login', { returnTo });
    window.location.href = response.data.auth_url;
  };

  const logout = async () => {
    await api.post('/api/auth/logout');
    setMe(EMPTY_ME);
    window.location.href = '/';
  };

  return <AuthContext.Provider value={{ me, loading, login, logout, refresh }}>{children}</AuthContext.Provider>;
};
